import { isEmptyObject } from "@entrilia/shared/utilities/objectHelper.ts";
import type { ConditionConfiguration, FieldConfiguration } from "../../../../../api/types/fieldConfigurationTypes.ts";
import { FieldConfigurationType } from "../../../../../api/types/fieldConfigurationTypes.ts";
import type {
  ChartConfiguration,
  FinancialStatementConfiguration,
  ListConfiguration,
  PivotConfiguration,
  ReportConfiguration,
  ReportType,
  StatementColumnsConfiguration,
  StatementLineConfiguration,
  TabularConfiguration,
  TileConfiguration,
} from "../../../../../api/types/svTypes.ts";
import { chartRelatedReportTypes } from "./chartRelatedReport.types.ts";
import {
  isFinancialStatementReportType,
  isListReportType,
  isPivotReportType,
  isTileReportType,
  usesTabularConfiguration,
} from "./reportTypeHelper.ts";
import { unique } from "./unique.ts";

export const getDimensionNames = (
  reportType: ReportType | undefined,
  configuration: ReportConfiguration | undefined
) => {
  if (configuration === undefined || reportType === undefined) {
    return [];
  }
  if (isPivotReportType(reportType)) {
    const { conditions, rows, columns } = configuration as PivotConfiguration;
    return getPivotDimensionNames(conditions, rows, columns);
  }
  if (usesTabularConfiguration(reportType)) {
    const { fields, conditions } = configuration as TabularConfiguration;
    return getTabularDimensionNames(fields, conditions);
  }
  if (chartRelatedReportTypes.includes(reportType)) {
    const { conditions, arguments: argumentFields } = configuration as ChartConfiguration;
    return getChartDimensionNames(conditions, argumentFields);
  }
  if (isTileReportType(reportType)) {
    const { fields, conditions } = configuration as TileConfiguration;
    return getTileDimensionNames(fields, conditions);
  }
  if (isListReportType(reportType)) {
    const { fields, conditions } = configuration as ListConfiguration;
    return getListDimensionNames(fields, conditions);
  }
  if (isFinancialStatementReportType(reportType)) {
    const { conditions, lines, columns } = configuration as FinancialStatementConfiguration;
    return getFinancialStatementDimensionNames(conditions, lines, columns);
  }
  throw new Error("Report type not supported");
};

export const getPivotDimensionNames = (
  conditions: ConditionConfiguration[],
  rows: FieldConfiguration[],
  columns: FieldConfiguration[]
) => {
  if (isEmptyObject(conditions) && isEmptyObject(rows) && isEmptyObject(columns)) {
    return [];
  }
  return conditions
    .map((c) => c.filter.dimensionName)
    .concat(rows.map((r) => r.name))
    .concat(columns.map((r) => r.name))
    .sort();
};

export const getTabularDimensionNames = (fields: FieldConfiguration[], conditions: ConditionConfiguration[]) => {
  if (isEmptyObject(fields) && isEmptyObject(conditions)) {
    return [];
  }
  const dimensionFields = fields
    .filter((f) => f.type === FieldConfigurationType.Dimension)
    .map((f) => f.name)
    .filter((f): f is string => !!f)
    .sort();

  return conditions
    .map((c) => c.filter.dimensionName)
    .concat(dimensionFields)
    .sort();
};

export const getChartDimensionNames = (conditions: ConditionConfiguration[], argumentFields: FieldConfiguration[]) => {
  if (isEmptyObject(conditions) && isEmptyObject(argumentFields)) {
    return [];
  }
  return conditions
    .map((c) => c.filter.dimensionName)
    .concat(argumentFields.map((r) => r.name))
    .sort();
};

export const getTileDimensionNames = (fields: FieldConfiguration[], conditions: ConditionConfiguration[]) => {
  return getTabularDimensionNames(fields, conditions);
};

export const getListDimensionNames = (fields: FieldConfiguration[], conditions: ConditionConfiguration[]) => {
  const dimensionFields = fields
    .filter(
      (field) => field.type === FieldConfigurationType.Dimension || field.type === FieldConfigurationType.Aggregation
    )
    .map((field) => field.name)
    .filter((field): field is string => !!field);

  return conditions
    .map((condition) => condition.filter.dimensionName)
    .concat(dimensionFields)
    .sort();
};

export const getFinancialStatementDimensionNames = (
  conditions: ConditionConfiguration[],
  lines: StatementLineConfiguration[],
  columns: StatementColumnsConfiguration
): string[] =>
  conditions
    .map((condition) => condition.filter.dimensionName)
    .concat(lines.flatMap((line) => (line.dimension ? [line.dimension.name] : [])))
    .concat(columns.axes.map((axis) => axis.field.name))
    .concat(
      columns.dataColumns.flatMap((column) =>
        column.customConditions.map((condition) => condition.filter.dimensionName)
      )
    )
    .filter(unique)
    .sort();
